import { Button, message, Tooltip, Dropdown } from 'antd';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../utils/axiosConfig';
import { useAppConfig } from './AppConfigProvider';
import ManualSubmitModal from './ManualSubmitModal';


const ActionBar = ({ selectedInvoices = [], onRefresh, currentStatus }) => {
  const { theme } = useAppConfig();
  const { t } = useTranslation();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [manualSubmitVisible, setManualSubmitVisible] = useState(false);

  const kebabMenuItems = [
    {
      key: 'openChinaTax',
      label: t('actionBar.openChinaTax'),
    },
    {
      key: 'manualSubmit',
      label: t('actionBar.manualSubmit'),
    },
  ];

  // Check if all selected invoices have status 'PENDING'
  const allPending = selectedInvoices.length > 0 &&
    selectedInvoices.every(invoice => invoice.status === 'PENDING' || invoice.status === 'ERROR');

  // Check if all selected invoices have status 'SUBMITTED'
  const allSubmitted = selectedInvoices.length > 0 &&
    selectedInvoices.every(invoice => invoice.status === 'SUBMITTED');

  // Check if all selected invoices are from the same customer (for merge button)
  const sameCustomer = selectedInvoices.length > 1 &&
    selectedInvoices.every(invoice =>
      invoice.customerName === selectedInvoices[0].customerName
    );

  // Button style based on the original action-button class
  const buttonStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: '12px',
    gap: '8px',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'all 0.3s',
  };

  const iconButtonStyle = {
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    padding: '0',
  };

  const handleKebabMenuClick = ({ key }) => {
    if (key === 'openChinaTax') {
      window.open('https://tpass.shanghai.chinatax.gov.cn:8443/#/login', '_blank', 'noopener,noreferrer');
    } else if (key === 'manualSubmit') {
      setManualSubmitVisible(true);
    }
  };

  // Handle submit action for invoices
  const handleSubmit = async () => {
    if (!allPending || selectedInvoices.length === 0 || submitLoading) return;

    setSubmitLoading(true);
    try {
      // Get current user or use a default
      const submittedBy = localStorage.getItem('username') || 'current_user';
      let allSuccessful = true;

      // Submit each invoice in sequence
      for (const invoice of selectedInvoices) {
        const response = await axiosInstance.post(`/invoice/${invoice.id}/submit`, { submittedBy });
        console.log(response);

        if (response.status !== 200) {
          allSuccessful = false;
          message.error(`Failed to submit invoice ${invoice.id}`);
        }
      }

      if (allSuccessful) {
        // Show success message
        message.success(`Successfully submitted ${selectedInvoices.length} invoice(s)`);
      }
    } catch (error) {
      console.error('Error submitting invoices:', error);
      message.error(`Error submitting invoices: ${error?.response?.data?.message}`);
    } finally {
      setSubmitLoading(false);
      // Refresh the data regardless of success or failure
      if (typeof onRefresh === 'function') {
        onRefresh();
      }
    }
  };

  // Handle export action for invoices
  const handleExport = async () => {
    if (!allPending || selectedInvoices.length === 0) return;
    try {
      const epicorIds = selectedInvoices.map(inv => inv.id);
      const response = await axiosInstance.post(
        '/excel/export',
        { epicorIds },
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'invoice_export.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      message.success('导出成功');
    } catch (error) {
      console.error('导出失败', error);
      message.error('导出失败，请重试');
    }
  };

  const handleRedNote = async () => {
    if (!allSubmitted || selectedInvoices.length === 0) return;

    try {
      const submittedBy = localStorage.getItem('username') || 'current_user';
      let allSuccessful = true;

      for (const invoice of selectedInvoices) {
        const response = await axiosInstance.post(`/invoice/${invoice.id}/red`, { submittedBy });

        if (response.status !== 200) {
          allSuccessful = false;
          message.error(`Failed to submit red note for invoice ${invoice.id}`);
        }
      }

      if (allSuccessful) {
        message.success(`Successfully submitted red note for ${selectedInvoices.length} invoice(s)`);
        if (typeof onRefresh === 'function') {
          onRefresh();
        }
      }
    } catch (error) {
      console.error('Error submitting red note:', error);
      message.error(`Error submitting red note: ${error?.response?.data?.error}`);
    }
  };

  const handleMerge = async () => {
    if (!sameCustomer || !allPending || selectedInvoices.length === 0 || mergeLoading) return;

    setMergeLoading(true);
    try {
      const submittedBy = localStorage.getItem('username') || 'current_user';
      const erpInvoiceIds = selectedInvoices.map(invoice => invoice.id);

      const response = await axiosInstance.post('/invoice/merge', {
        erpInvoiceIds,
        submittedBy
      });

      if (response.status === 200) {
        message.success(`Successfully merged ${selectedInvoices.length} invoice(s)`);
      } else {
        message.error('Failed to merge invoices');
      }
    } catch (error) {
      console.error('Error merging invoices:', error);
      message.error(`Error merging invoices: ${error?.response?.data?.message || error.message}`);
    } finally {
      setMergeLoading(false);
      // Refresh the data regardless of success or failure
      if (typeof onRefresh === 'function') {
        onRefresh();
      }
    }
  };

  console.log("currentStatus", currentStatus);

  return (
    <div className="action-bar">
      <style>
        {`
          /* Scope default action buttons to this bar and exclude kebab menu */
          .action-bar .ant-btn:not(.kebab-button) {
            background-color: ${theme.primary} !important;
            color: rgba(255, 255, 255, 0.95) !important;
            border: none;
            box-shadow: none !important;
          }
          
          .action-bar .ant-btn:not(.kebab-button):hover:not(:disabled) {
            background-color: ${theme.primary}CC !important;
            color: white !important;
          }
          
          .action-bar .ant-btn:not(.kebab-button):hover:not(:disabled) .icon-medium {
            color: white !important;
          }
          
          .action-bar .ant-btn:not(.kebab-button):active:not(:disabled) {
            background-color: ${theme.primary}EE !important;
            color: white !important;
          }
          
          .action-bar .ant-btn:not(.kebab-button):disabled {
            background-color:#FAFAFA !important;
            color: #B3B3B3 !important;
            border:#CCCCCC 1px solid !important;
          }
          
          .action-bar .ant-btn:not(.kebab-button):disabled .icon-medium {
            color: #B3B3B3 !important;
          }

          /* Kebab menu button */
          .action-bar .kebab-button,
          .action-bar .kebab-button:hover,
          .action-bar .kebab-button:active,
          .action-bar .kebab-button:focus,
          .action-bar .kebab-button:focus-visible,
          .action-bar .kebab-button.ant-dropdown-open {
            background-color: transparent !important;
            border: none !important;
            box-shadow: none !important;
            padding: 0 !important;
            width: 24px !important;
            height: 24px !important;
            min-width: 24px !important;
            margin-top: 8px !important;
            outline: none !important;
          }
          /* remove antd click wave effect */
          .action-bar .kebab-button.ant-btn::before,
          .action-bar .kebab-button.ant-btn::after {
            display: none !important;
          }
          .action-bar .kebab-button .material-symbols-outlined {
            color: ${theme.primary} !important;
          }

          /* Dropdown menu under action bar*/
          .actionbar-dropdown .ant-dropdown-menu {
            border-radius: 4px !important;
          }
        `}
      </style>
      <div className="action-group">

        <>
          {(!currentStatus || currentStatus === 'PENDING' || currentStatus === 'ERROR') && (
            <Tooltip title={!sameCustomer ? "At least 2 invoices from the same customer are required to merge" : ""}>
              <Button
                style={buttonStyle}
                disabled={!sameCustomer || !allPending}
                loading={mergeLoading}
                onClick={handleMerge}
                icon={<span style={{ color: !sameCustomer || !allPending ? '#808080' : '#ffffff', cursor: 'pointer', fontVariationSettings: "'FILL' 0, 'wght' 200, 'GRAD' 0, 'opsz' 24" }} className="material-symbols-outlined">merge</span>}
              >
                {t('actionBar.merge')}
              </Button>
            </Tooltip>
          )}

          {(!currentStatus || currentStatus === 'PENDING' || currentStatus === 'ERROR') && (
            <Button
              style={buttonStyle}
              disabled={!allPending}
              loading={submitLoading}
              onClick={() => {
                handleSubmit();
              }}
              icon={<span style={{ color: !allPending ? '#808080' : '#ffffff', cursor: 'pointer', fontVariationSettings: "'FILL' 0, 'wght' 200, 'GRAD' 0, 'opsz' 24" }} className="material-symbols-outlined">api</span>}
            >
              {t('actionBar.submit')}
            </Button>
          )}

          {(!currentStatus || currentStatus === 'PENDING' || currentStatus === 'ERROR') && (
            <Dropdown
              menu={{ items: kebabMenuItems, onClick: handleKebabMenuClick }}
              trigger={['click']}
              overlayClassName="actionbar-dropdown"
            >
              <Button
                type="text"
                className="kebab-button"
                style={{ display: 'none' }} // temporarily hide kebab menu button
                icon={(
                  <span
                    style={{ cursor: 'pointer', fontVariationSettings: "'FILL' 0, 'wght' 200, 'GRAD' 0, 'opsz' 24" }}
                    className="material-symbols-outlined"
                  >
                    more_vert
                  </span>
                )}
              />
            </Dropdown>
          )}

          {/* {(!currentStatus || currentStatus === 'SUBMITTED') && (
            <Button
              style={buttonStyle}
              disabled={!allSubmitted}
              onClick={handleRedNote}
              icon={<span style={{ color: !allSubmitted ? '#808080' : '#ffffff', cursor: 'pointer', fontVariationSettings: "'FILL' 0, 'wght' 200, 'GRAD' 0, 'opsz' 24" }} className="material-symbols-outlined">block</span>}
            >
              {t('actionBar.redNote')}
            </Button>
          )} */}
        </>



      </div>

      <div className="action-group">
        <Button
          style={buttonStyle}
          disabled={!allPending}
          onClick={handleExport}
          icon={<span style={{ color: !allPending ? '#808080' : '#ffffff', cursor: 'pointer', fontVariationSettings: "'FILL' 0, 'wght' 200, 'GRAD' 0, 'opsz' 24" }} className="material-symbols-outlined">table</span>}
        >
          {t('actionBar.export')}
        </Button>

      </div>

      <ManualSubmitModal
        visible={manualSubmitVisible}
        onClose={() => setManualSubmitVisible(false)}
        invoiceId={selectedInvoices[0]?.einvoiceId || selectedInvoices[0]?.id}
      />
    </div>
  );
};

export default ActionBar; 